import { useState, useRef } from 'react';
import { Info, MousePointer, Keyboard, Eye } from 'lucide-react';

function ChartInteractionDemo({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('interactions');

  if (!isOpen) return null;

  const interactionGuide = [
    {
      action: 'Single Click',
      description: 'Drill down into detailed data or navigate to next level',
      icon: MousePointer,
      example: 'Click on a department bar to see category breakdown'
    },
    {
      action: 'Double Click',
      description: 'Open detailed view modal with comprehensive information',
      icon: Eye,
      example: 'Double-click on any chart element for detailed analysis'
    },
    {
      action: 'Ctrl/Cmd + Click',
      description: 'Multi-select elements for comparison or detailed view',
      icon: Keyboard,
      example: 'Hold Ctrl and click multiple departments to compare'
    },
    {
      action: 'Right Click',
      description: 'Open context menu with additional actions',
      icon: MousePointer,
      example: 'Right-click for alerts and analysis options'
    },
    {
      action: 'Hover',
      description: 'Enhanced tooltips with trend information and glow effects',
      icon: MousePointer,
      example: 'Hover over data points to see detailed tooltips'
    }
  ];

  const animationFeatures = [
    {
      name: 'Smooth Entry',
      description: 'Staggered animations when charts first load',
      demo: 'Charts fade in with smooth scaling and opacity transitions'
    },
    {
      name: 'Drill-Down Transitions',
      description: 'Animated transitions when navigating between data levels',
      demo: 'Zoom and fade effects when drilling into categories'
    },
    {
      name: 'Hover Effects',
      description: 'Interactive glow and scale effects on hover',
      demo: 'Elements glow and slightly scale when hovered'
    },
    {
      name: 'Data Updates',
      description: 'Morphing animations when data changes',
      demo: 'Smooth transitions between different view types'
    },
    {
      name: 'Click Feedback',
      description: 'Ripple and particle effects on interaction',
      demo: 'Visual feedback with ripples and particles on click'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden border"
        style={{ 
          background: 'var(--panel)', 
          borderColor: 'var(--border)' 
        }}
      >
        {/* Header */}
        <div className="p-6 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info className="w-6 h-6" style={{ color: 'var(--primary)' }} />
              <h2 className="text-xl font-semibold" style={{ color: 'var(--text)' }}>
                Advanced Chart Interactions Guide
              </h2>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded hover:opacity-80"
              style={{ 
                background: 'var(--border)', 
                color: 'var(--text)' 
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
          {[
            { id: 'interactions', label: 'Interactions', icon: MousePointer },
            { id: 'animations', label: 'Animations', icon: Eye },
            { id: 'info', label: 'Chart Info', icon: Info }
          ].map(tab => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                  activeTab === tab.id ? 'border-b-2' : ''
                }`}
                style={{
                  color: activeTab === tab.id ? 'var(--primary)' : 'var(--muted)',
                  borderColor: activeTab === tab.id ? 'var(--primary)' : 'transparent'
                }}
              >
                <IconComponent className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'interactions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>
                  Interactive Chart Controls
                </h3>
                <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
                  Our charts support multiple interaction methods for enhanced data exploration:
                </p>
              </div>

              <div className="grid gap-4">
                {interactionGuide.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <div 
                      key={index}
                      className="p-4 rounded-lg border"
                      style={{ 
                        background: 'var(--bg)', 
                        borderColor: 'var(--border)' 
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <IconComponent className="w-5 h-5 mt-1" style={{ color: 'var(--primary)' }} />
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1" style={{ color: 'var(--text)' }}>
                            {item.action}
                          </h4>
                          <p className="text-sm mb-2" style={{ color: 'var(--muted)' }}>
                            {item.description}
                          </p>
                          <div 
                            className="text-xs px-2 py-1 rounded"
                            style={{ 
                              background: 'var(--panel)', 
                              color: 'var(--text)' 
                            }}
                          >
                            Example: {item.example}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'animations' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>
                  Animation Features
                </h3>
                <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
                  Enhanced visual feedback and smooth transitions throughout the dashboard:
                </p>
              </div>

              <div className="grid gap-4">
                {animationFeatures.map((feature, index) => (
                  <div 
                    key={index}
                    className="p-4 rounded-lg border"
                    style={{ 
                      background: 'var(--bg)', 
                      borderColor: 'var(--border)' 
                    }}
                  >
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--text)' }}>
                      {feature.name}
                    </h4>
                    <p className="text-sm mb-2" style={{ color: 'var(--muted)' }}>
                      {feature.description}
                    </p>
                    <div 
                      className="text-xs px-2 py-1 rounded"
                      style={{ 
                        background: 'var(--panel)', 
                        color: 'var(--text)' 
                      }}
                    >
                      {feature.demo}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'info' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>
                  Export Information
                </h3>
                <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
                  All chart data is included in the main dashboard export. Use the Export button in the dashboard header to download a comprehensive Excel file with all charts and data.
                </p>
              </div>
              
              <div className="p-4 rounded-lg border" style={{ 
                background: 'var(--bg)', 
                borderColor: 'var(--border)' 
              }}>
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text)' }}>
                  📊 Main Dashboard Export
                </h4>
                <p className="text-sm mb-3" style={{ color: 'var(--muted)' }}>
                  Click the "Export" button in the dashboard header to download an Excel file containing:
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Dashboard Summary',
                    'Department Breakdown', 
                    'Monthly Trends',
                    'Category Breakdown',
                    'Top Pending Items'
                  ].map((item, index) => (
                    <span
                      key={index}
                      className="text-xs px-2 py-1 rounded"
                      style={{ 
                        background: 'var(--primary)', 
                        color: 'white' 
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t text-center" style={{ borderColor: 'var(--border)' }}>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>
            Try these interactions on any chart in the dashboard. Click the info button (ℹ) on chart headers to access this guide.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ChartInteractionDemo;